import cx from 'classnames';
import {find, indexOf, map, transform} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CellProps} from 'recharts';

import {PieChart} from 'components';
import {getPercentage} from 'utils/transform';

export type PieChartMetricConfig<TMetric, TData> = {
    metric: TMetric;
    color: string;
    ratioColor: string;
    title: string;
    valueGetter: (data: TData) => number;
    totalGetter: (data: TData) => number;
}

export type PieChartData<M> = {
    metric: M,
    value: number
}

type PieChartConfig<TData, TMetric extends string> = {
    metric: TMetric;
    color: string;
    ratioColor: string;
    title: string;
    valueGetter: (data: TData) => number;
    totalGetter: (data: TData) => number;
};

type Props<TData, TConfigs extends PieChartConfig<TData, string>> = {
    data: TData;
    title: string;
    configs: TConfigs[];
    activeMetric: TConfigs['metric'];
    onMetricClick: (metric: TConfigs['metric']) => void;
};

export const MetricsPieChart = <TData, TConfigs extends PieChartConfig<TData, string>>(
    {data, title, configs, activeMetric, onMetricClick}: Props<TData, TConfigs>
) => {
    const {t} = useTranslation();

    const [chartData, setChartData] = useState<PieChartData<any>[]>([]);
    const [cells, setCells] = useState<CellProps[]>([]);

    const metrics = useMemo(() => map(configs, (config) => config.metric), [configs]);
    const defaultValue = useMemo(() => transform(configs, (acc, config) => acc.push({name: config.metric, value: 0}), []), [configs]);
    const total = useMemo(() => configs?.[0]?.totalGetter?.(data), [configs, data]);

    const activeIndex = useMemo(() => indexOf(metrics, activeMetric), [metrics, activeMetric]);

    const getMetricsChartData = useCallback(() =>
        !data ? defaultValue : transform<PieChartMetricConfig<any, any>, PieChartData<any>[]>(
            configs, (acc: PieChartData<any>[], config) => {
                if (!metrics.includes(config.metric)) {
                    return;
                }
                acc.push({
                    metric: config.metric, value: config.valueGetter(data) ||
                find(defaultValue, {name: config.metric})?.value
                });
            }, []),
    [data, defaultValue, configs, metrics]);

    useEffect(() => {
        setChartData(getMetricsChartData());
    }, [data, getMetricsChartData]);

    useEffect(() => {
        const cellsConfig: CellProps[] = configs.map(({metric, color, ratioColor}) => ({
            stroke: ratioColor || color,
            fill: ratioColor || color,
            ...(onMetricClick && {cursor: 'pointer', onClick: () => onMetricClick(metric)})
        }));
        setCells(cellsConfig);
    }, [t, configs, onMetricClick]);

    return (
        <div className='tw-ts-h5 tw-mb-6'>
            <div className='tw-text-center tw-flex-center'>
                <span className='tw-ts-h3'>{title}</span>
            </div>
            <div className='tw-h-72'>
                <PieChart
                    innerRadius={65}
                    outerRadius={130}
                    startAngle={90}
                    endAngle={-270}
                    margin={{top: 20, right: 20, left: -10, bottom: 5}}
                    noDataCell={{fill: '#fff', stroke: '#E0E0E0'}}
                    cells={cells}
                    data={chartData}
                    innerLabel={total}
                    innerLabelClass='tw-font-bold'
                    activeIndex={activeIndex}
                />
            </div>
            <div className='tw-table tw-mx-auto tw-mt-8'>
                {map(cells, (cell, i) => {
                    const config = configs[i];
                    const metric = config.metric;
                    const value = find(chartData, (dataItem) => dataItem.metric === metric)?.value;
                    const isActive = i === activeIndex;
                    return (
                        <div
                            key={i}
                            className={cx(
                                'tw-table-row tw-h-10', {
                                    'tw-cursor-pointer': !!onMetricClick,
                                    'hover:tw-text-primary-light': !!onMetricClick && !isActive,
                                    'tw-text-primary tw-font-bold': isActive
                                })}
                            onClick={() => onMetricClick?.(metric)}
                        >
                            <div className='tw-table-cell tw-pr-3 tw-align-middle'>
                                <div className='tw-flex tw-items-center'>
                                    <div
                                        className='tw-w-4 tw-h-4 tw-inline-block tw-rounded-full tw-mr-2'
                                        style={{backgroundColor: cell.fill}}
                                    />
                                    {t(config.title)}
                                </div>
                            </div>
                            <div className='tw-table-cell tw-pr-3 tw-align-middle'>{value}</div>
                            <div className='tw-table-cell tw-align-middle'>{`(${getPercentage(value, total)}%)`}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
